import { FiUsers, FiSun, FiMoon, FiMenu, FiX, FiPackage, FiBriefcase, FiMessageSquare, FiChevronDown, FiChevronRight, FiLogOut, FiTrendingUp, FiActivity, FiAward, FiTag } from "react-icons/fi";
import { LiaFileInvoiceDollarSolid } from "react-icons/lia";
import { TbLayoutRows } from "react-icons/tb";
import { IoChatbubbleEllipsesOutline, IoNewspaperOutline } from "react-icons/io5";
import { LuClipboardList } from "react-icons/lu";
import { RiImageLine } from "react-icons/ri";
import { IoMdNotificationsOutline } from "react-icons/io";
import { HiOutlineMenuAlt2, HiOutlineMenuAlt3 } from "react-icons/hi";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { menuActions } from "../store/menuSlice";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useState } from "react";
import { authActions } from "../store/authSlice";
import { FaBuilding, FaCrown, FaGem } from "react-icons/fa";
import { MdOutlineStarRate, MdPerson, MdCampaign } from "react-icons/md";
import { FaUserShield, FaChartBar } from "react-icons/fa";
import { FiShield } from "react-icons/fi";
import { selectedListingsActions } from "../store/selectedListingsSlice";
import { listingApi } from "../store/services/listingServices";
import { userListingsApi } from "../store/services/userListingsServices";
import { userApi } from "../store/services/userServices";
import { blogApi } from "../store/services/blogServices";
import { chatApi } from "../store/services/chatsServices";
import { ratingApi } from "../store/services/ratingServices";
import { campaignApi } from "../store/services/campaignsServices";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../hooks/usePermissions";
import { useGetPlanStatsQuery } from "../store/services/companyPlanServices";
import api from "../api/axiosInstance";

// Menu configuration
// Re-enabled 2026-09-10 at the client's request.
const SHOW_AGENCY_MODULE = true;

// Sidebar structure agreed with the client (spec 2026-10-26): the same eight
// groups, in the same order, as the veturascout dashboard. Every item keeps the
// roles/module gating it had before the regroup — only its position and some
// labels changed (Company Plans -> Company Subscriptions, Packages -> Individual
// Packages, Plans & Pricing Company -> Company Packages).
const MENU_GROUPS = [
    {
        key: 'overview',
        label: 'Overview',
        items: [
            {
                key: 'dashboard',
                path: '/',
                icon: FaUserShield,
                label: 'Dashboard',
                roles: ['super_admin']
            },
            {
                key: 'platform-analytics',
                path: '/platform-analytics',
                icon: FiTrendingUp,
                label: 'Platform Analytics',
                roles: ['super_admin']
            },
        ]
    },
    {
        key: 'users',
        label: 'Users',
        items: [
            {
                key: 'users',
                path: '/users',
                icon: FiUsers,
                label: 'Users',
                roles: ['super_admin', 'company_admin', 'agent']
            },
            ...(SHOW_AGENCY_MODULE ? [{
                key: 'agency',
                path: '/agency',
                icon: FaBuilding,
                label: 'Companies',
                roles: ['super_admin', 'company_admin']
            }] : []),
            {
                // Platform staff manage agents inside Company Subscriptions now
                // (client spec: "remove the Agents rubric"), so the entry is
                // gone for super_admin and scoped admins. A company's own
                // admin/agents still get it — for them this IS their team page.
                key: 'agent',
                path: '/agent',
                icon: MdPerson,
                label: 'Agent',
                roles: ['company_admin', 'agent'],
                staffHidden: true
            },
        ]
    },
    {
        key: 'listings',
        label: 'Listings',
        items: [
            {
                key: 'listings',
                path: '/listings',
                icon: TbLayoutRows,
                label: 'Listings',
                roles: ['super_admin', 'company_admin', 'agent', 'marketing']
            },
            {
                key: 'top-listings',
                path: '/top-listings',
                icon: FaCrown,
                label: 'TOP Listings',
                roles: ['super_admin']
            },
            {
                // Moderation queue for the EXCLUSIVE carousel on the public
                // search page.
                key: 'exclusive-listings',
                path: '/exclusive-listings',
                icon: FaGem,
                label: 'Exclusive Listings',
                roles: ['super_admin']
            },
        ]
    },
    {
        key: 'subscriptions',
        label: 'Subscriptions & Payments',
        items: [
            {
                // Account-level FREE/TOP/GOLD subscriptions. A different product
                // from Individual Packages below, which is the per-LISTING
                // promotion an owner buys for one property.
                key: 'company-plans',
                path: '/company-plans',
                icon: FiBriefcase,
                label: 'Company Subscriptions',
                roles: ['super_admin']
            },
            {
                // GOLD is sold by hand, so the request queue gets its own entry
                // rather than living only behind a tab on Company Subscriptions —
                // it is a sales inbox worked through daily.
                key: 'plan-leads',
                path: '/plan-leads',
                icon: FiMessageSquare,
                label: 'GOLD Enquiries',
                roles: ['super_admin']
            },
            {
                key: 'payments',
                path: '/payments',
                icon: LiaFileInvoiceDollarSolid,
                label: 'Payments',
                roles: ['super_admin', 'company_admin', 'agent']
            },
            {
                key: 'packages',
                path: '/packages',
                icon: FiPackage,
                label: 'Individual Packages',
                roles: ['super_admin']
            },
            // Hidden from the dashboard 2026-09-10 at the client's request.
            // {
            //     key: 'custom-packages',
            //     path: '/custom-packages',
            //     icon: FiPackage,
            //     label: 'Custom Packages',
            //     roles: ['super_admin']
            // },
            {
                // The plan catalogue on its own page, like veturascout's:
                // each plan's texts in three languages, prices and features.
                key: 'plans',
                module: 'company-plans',
                path: '/plans',
                icon: FiTag,
                label: 'Company Packages',
                roles: ['super_admin']
            },
            {
                // The individual TRADER plan ("Pako Tregtare") — its own entry,
                // not a row in the agency catalogue: it is sold to private
                // sellers, not agencies.
                key: 'trader-plans',
                module: 'company-plans',
                path: '/trader-plans',
                icon: FiTag,
                label: 'Pako Tregtare',
                roles: ['super_admin']
            },
        ]
    },
    {
        key: 'marketing',
        label: 'Marketing',
        items: [
            {
                /* Roles here match hasBannerAccess on the backend exactly
                   (super_admin / admin / marketing). Listing a role the API
                   would 403 just puts a dead entry in someone's sidebar. */
                key: 'banners',
                path: '/banners',
                icon: RiImageLine,
                label: 'Banners',
                roles: ['super_admin', 'marketing']
            },
            {
                // The homepage "Partnerët tanë" strip. Also a tab on Company
                // Subscriptions; it gets its own entry because it is site
                // content. `module` is the permission it is gated on for scoped
                // admins — the same one the API checks.
                key: 'partners',
                module: 'company-plans',
                path: '/partners',
                icon: FiAward,
                label: 'Homepage Partners',
                roles: ['super_admin']
            },
            {
                key: 'blogs',
                path: '/blogs',
                icon: IoNewspaperOutline,
                label: 'Blog',
                roles: ['super_admin', 'company_admin', 'agent', 'marketing']
            },
            {
                key: 'campaigns',
                path: '/campaigns',
                icon: MdCampaign,
                label: 'Campaigns',
                roles: ['super_admin']
            },
            {
                key: 'notifications',
                path: '/notifications',
                icon: IoMdNotificationsOutline,
                label: 'Push Notifications',
                roles: ['super_admin']
            },
        ]
    },
    {
        key: 'user-activity',
        label: 'User Activity',
        items: [
            {
                key: 'delete_reasons',
                path: '/delete_reasons',
                icon: LuClipboardList,
                label: 'Delete Reasons',
                roles: ['super_admin']
            },
            {
                key: 'chats',
                path: '/chats',
                icon: IoChatbubbleEllipsesOutline,
                label: 'Chats',
                roles: ['super_admin', 'company_admin', 'agent']
            },
            {
                key: 'ratings',
                path: '/ratings',
                icon: MdOutlineStarRate,
                label: 'Ratings',
                roles: ['super_admin', 'company_admin', 'agent']
            },
        ]
    },
    {
        key: 'reports',
        label: 'Reports',
        items: [
            {
                key: 'reports',
                path: '/reports',
                icon: FaChartBar,
                label: 'Reports',
                roles: ['super_admin', 'company_admin', 'agent', 'marketing']
            },
        ]
    },
    {
        key: 'administration',
        label: 'Administration',
        items: [
            {
                /* super_admin only, matching the backend guard on
                   /api/audit-logs: the entries carry full row snapshots and
                   span every user on the platform. */
                key: 'audit-logs',
                path: '/audit-logs',
                icon: FiActivity,
                label: 'Audit Logs',
                roles: ['super_admin']
            },
            {
                /* Granting access is the one thing a scoped admin must never do,
                   or the permission matrix is decorative — see the note in
                   config/adminPermissions.js. Deliberately not a module, so an
                   `admin` can never be granted it. */
                key: 'admin-access',
                path: '/admin-access',
                icon: FiShield,
                label: 'Admin Access',
                roles: ['super_admin']
            },
        ]
    },
];

// Flattened list for path -> active category lookups
const MENU_ITEMS = MENU_GROUPS.flatMap((group) => group.items);

/* Sidebar row. Defined at module scope rather than inside Menu(): a component
   declared in the render body is a new type on every render, so React throws
   the whole nav away and rebuilds it each time the badge poll ticks. */
const MenuItem = ({ item, isActive, isExpanded, badge = 0, onClick }) => {
    const Icon = item.icon;
    const tooltip = badge > 0 ? `${item.label} — ${badge} waiting` : item.label;

    return (
        <div onClick={onClick} title={!isExpanded ? tooltip : undefined}>
            <Link
                to={item.path}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative
                    ${isActive
                        ? "bg-gradient-to-r from-primary/20 to-primary/10 dark:from-primary/30 dark:to-primary/20"
                        : "hover:bg-gray-100 dark:hover:bg-zinc-800/80"
                    }`}
            >
                {/* Active indicator bar */}
                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full transition-all duration-300
                    ${isActive ? "h-8 bg-primary" : "h-0 bg-transparent"}`}
                />

                {/* Icon container */}
                <div className={`relative flex-none flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200
                    ${isActive
                        ? "bg-primary text-white shadow-lg shadow-primary/30"
                        : "bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-zinc-700 group-hover:text-gray-700 dark:group-hover:text-gray-300"
                    }`}
                >
                    <Icon className="w-5 h-5" />
                    {/* Collapsed there is no room for a number, so the icon carries a
                        dot instead — the point is "something is waiting", and the
                        count is one hover away. */}
                    {badge > 0 && !isExpanded && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-600 ring-2 ring-white dark:ring-zinc-900" />
                    )}
                </div>

                {/* Label */}
                {isExpanded && (
                    <span className={`text-sm font-medium whitespace-nowrap transition-all duration-200
                        ${isActive
                            ? "text-gray-900 dark:text-white"
                            : "text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                        }`}
                    >
                        {item.label}
                    </span>
                )}

                {/* Work waiting on a human. Red because it is a queue, not a stat —
                    it takes the trailing slot ahead of the active chevron, since a
                    number to act on outranks decoration. */}
                {isExpanded && badge > 0 ? (
                    <span className="ml-auto min-w-[22px] px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold text-center leading-tight">
                        {badge > 99 ? "99+" : badge}
                    </span>
                ) : (
                    isExpanded && isActive && <FiChevronRight className="w-4 h-4 text-primary ml-auto" />
                )}
            </Link>
        </div>
    );
};

export default function Menu() {
    const isBigMenu = useSelector((state) => state.menu.isBigMenu);
    const token = localStorage.getItem("prona_userToken");
    const [isLogoutScreenActive, setIsLogoutScreenActive] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();

    const user = jwtDecode(token);
    const userRole = user.role;
    const isSuperAdmin = userRole === 'super_admin';

    const { can, isScopedAdmin } = usePermissions();

    const dispatch = useDispatch();

    /* ------------------------------------------------- queue badges ---
     * Unworked GOLD enquiries and pending exclusive nominations were only
     * visible after opening the pages themselves; nothing in the sidebar
     * hinted that anything needed doing.
     *
     * Both counts are skipped entirely for anyone who cannot open those
     * pages, so the sidebar does not fire a 403 on every route for them.
     * Both are polled rather than fetched once: the sidebar is always
     * mounted, and a stale "3 waiting" after somebody has worked the queue
     * is worse than no badge at all. */
    const { data: planStatsRes } = useGetPlanStatsQuery(undefined, {
        skip: !isSuperAdmin,
        pollingInterval: 60000,
        refetchOnMountOrArgChange: true,
    });

    /* Derived exactly the way CompanyPlansPage derives it, so the badge and
       the page can never disagree. */
    const newLeads = useMemo(() => {
        const rows = planStatsRes?.data?.leads_by_status;
        if (!Array.isArray(rows)) return 0;
        return Number(rows.find((r) => r?.status === 'new')?.c) || 0;
    }, [planStatsRes]);

    /* The exclusive queue has no RTK Query service of its own — the page
       reads it through the shared axios instance, so the badge does too.
       /stats does not carry a pending-exclusive count, but the queue
       endpoint returns per-status totals alongside its rows, so limit=1 is
       enough to read the count without pulling the queue itself. */
    const [pendingExclusive, setPendingExclusive] = useState(0);

    useEffect(() => {
        if (!isSuperAdmin) return undefined;

        let cancelled = false;
        const load = async () => {
            try {
                const res = await api.get('/company-plans-admin/exclusive-slots', {
                    params: { status: 'pending', limit: 1 },
                });
                if (!cancelled) setPendingExclusive(Number(res.data?.counts?.pending || 0));
            } catch (e) {
                // A badge is not worth a broken sidebar; fall back to "nothing waiting".
                if (!cancelled) setPendingExclusive(0);
            }
        };

        load();
        const timer = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [isSuperAdmin]);

    const badges = {
        'plan-leads': newLeads,
        'exclusive-listings': pendingExclusive,
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    /* Highlighting follows the ROUTE, not the last thing that was clicked.
       It used to be read off the store, which only a click ever wrote to (and
       only once, on mount, from window.location) — so arriving through a Link
       inside a page, the browser Back button or a pasted URL left the sidebar
       lit on whatever had been opened before. */
    const activeKey = useMemo(() => {
        // Exact match first, then the longest matching base path so that a
        // nested route (/chats/123, /company-plans/companies/5) lights its own
        // entry rather than a shorter prefix that happens to also match.
        const exact = MENU_ITEMS.find((item) => item.path === pathname);
        if (exact) return exact.key;

        const nested = MENU_ITEMS
            .filter((item) => item.path !== '/' && pathname.startsWith(`${item.path}/`))
            .sort((a, b) => b.path.length - a.path.length)[0];

        return nested ? nested.key : '';
    }, [pathname]);

    // menuSlice still owns activeCategory, so keep it in step with the route.
    useEffect(() => {
        if (activeKey) {
            dispatch(menuActions.changeActiveCategory({ category: activeKey }));
        }
    }, [activeKey, dispatch]);

    const handleCategorySelect = (category) => (e) => {
        e.stopPropagation();
        dispatch(menuActions.changeActiveCategory({ category }));
        setIsDetailsExpanded(false);
        closeMobileMenu();
    };

    const handleMenuChange = () => {
        if (!isBigMenu) {
            setIsLogoutScreenActive(false);
        }
        dispatch(menuActions.changeMenuSize());
    };

    const handleProfileClick = (e) => {
        e.stopPropagation();
        if (!isBigMenu) {
            dispatch(menuActions.changeMenuSize());
            return;
        }
        setIsLogoutScreenActive(!isLogoutScreenActive);
    };

    const handleLogout = () => {
        // Clear auth state
        dispatch(authActions.removeToken());

        // Clear localStorage
        localStorage.removeItem("prona_userToken");

        // Reset menu state
        dispatch(menuActions.resetMenu());

        // Clear selected listings
        dispatch(selectedListingsActions.clearSelectedListings());

        // Reset RTK Query caches
        dispatch(listingApi.util.resetApiState());
        dispatch(userListingsApi.util.resetApiState());
        dispatch(userApi.util.resetApiState());
        dispatch(blogApi.util.resetApiState());
        dispatch(chatApi.util.resetApiState());
        dispatch(ratingApi.util.resetApiState());
        dispatch(campaignApi.util.resetApiState());

        // Reset local state
        setIsLogoutScreenActive(false);
        setIsDetailsExpanded(false);

        // Navigate to login
        navigate("/login");
    };

    /* Which entries this account sees.
     *
     * For a SCOPED ADMIN the per-module matrix decides and the `roles` arrays
     * are bypassed entirely — those arrays predate the matrix and name no
     * 'admin', so honouring them as well would leave every scoped admin with an
     * empty sidebar no matter what they were granted.
     *
     * Every other role is untouched: super_admin, marketing, company_admin and
     * agent keep exactly the role-based gating they had. */
    const canSeeItem = (item) =>
        isScopedAdmin
            ? !item.staffHidden && can(item.module || item.key, 'view')
            : item.roles.includes(userRole);

    // Filter groups (and their items); drop any group left empty.
    const filteredGroups = MENU_GROUPS
        .map((group) => ({
            ...group,
            items: group.items.filter(canSeeItem),
        }))
        .filter((group) => group.items.length > 0);

    const showExpanded = isBigMenu || isMobileMenuOpen;

    /* A company account has no first/last name, so fall back to the company
       name before giving up — an avatar reading "?" for every agency account
       is worse than no avatar at all. */
    const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim()
        || user.company_name
        || user.email
        || "";

    const initials = displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("") || "?";

    const roleLabel = userRole ? userRole.replace(/_/g, " ") : "";

    return (
        <>
            {/* Mobile Header Bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-gray-50 dark:bg-gray-800 shadow-sm z-40 flex items-center justify-between px-4">
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-200/70 dark:hover:bg-gray-700 transition-colors"
                >
                    {isMobileMenuOpen ? (
                        <FiX className="text-2xl text-gray-600 dark:text-gray-300" />
                    ) : (
                        <FiMenu className="text-2xl text-gray-600 dark:text-gray-300" />
                    )}
                </button>
                <img
                    src="/pics/Frame 1618869319.svg"
                    alt="Logo"
                    className="w-10 h-10 object-contain"
                />
            </div>

            {/* Mobile Backdrop */}
            {isMobileMenuOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Sidebar Menu */}
            <div
                className={`
                    w-[280px] ${isBigMenu ? "md:w-[260px]" : "md:w-[88px]"}
                    transition-all duration-300 ease-in-out h-dvh md:h-full flex-none flex flex-col overflow-hidden
                    rounded-none md:rounded-2xl
                    bg-white dark:bg-zinc-900/95
                    shadow-xl shadow-gray-200/50 dark:shadow-zinc-900/50
                    border border-gray-100 dark:border-zinc-800
                    fixed md:relative z-50 md:z-auto
                    top-0 left-0
                    ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
                `}
            >
                {/* Header + nav (nav scrolls when it doesn't fit, e.g. small laptops) */}
                <div className="flex flex-col flex-1 min-h-0">
                    <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-zinc-800 flex-none">
                        <div className="flex items-center gap-3 min-w-0">
                            <img
                                src="/pics/Frame 1618869319.svg"
                                alt="Logo"
                                width={42}
                                className="flex-none transition-all duration-200"
                            />
                            {showExpanded && (
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white truncate">Prona Scout</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Dashboard</span>
                                </div>
                            )}
                        </div>
                        {/* Collapse toggle. Desktop only — on mobile the drawer is
                            driven by the top bar's hamburger, and a second control
                            that shrinks a full-screen drawer means nothing. */}
                        <button
                            onClick={handleMenuChange}
                            className="hidden md:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                            title={isBigMenu ? "Collapse menu" : "Expand menu"}
                        >
                            {isBigMenu ? (
                                <HiOutlineMenuAlt2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            ) : (
                                <HiOutlineMenuAlt3 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            )}
                        </button>
                    </div>

                    {/* Notifications bell removed 2026-09-09 at the user's
                        request. The component file is still on disk and the
                        backend feed (/admin/notifications) is untouched, so
                        rendering it here again is all that is needed to bring
                        it back. Dropping it also stops the once-a-minute poll
                        of that endpoint. */}

                    {/* Menu Items — grouped into labelled sections */}
                    <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 flex flex-col gap-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {filteredGroups.map((group, groupIndex) => (
                            <div key={group.key} className="flex flex-col gap-1">
                                {showExpanded ? (
                                    <div className="px-3 py-2">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                            {group.label}
                                        </span>
                                    </div>
                                ) : (
                                    groupIndex !== 0 && (
                                        <div className="mx-auto my-1.5 h-px w-6 rounded-full bg-gray-200 dark:bg-zinc-700" />
                                    )
                                )}
                                {group.items.map((item) => (
                                    <MenuItem
                                        key={item.key}
                                        item={item}
                                        isActive={activeKey === item.key}
                                        isExpanded={showExpanded}
                                        badge={badges[item.key] || 0}
                                        onClick={handleCategorySelect(item.key)}
                                    />
                                ))}
                            </div>
                        ))}
                    </nav>
                </div>

                {/* Footer Section */}
                <div className="p-4 border-t border-gray-100 dark:border-zinc-800 flex-none">
                    {/* Dark Mode Toggle */}
                    <div className={`flex items-center ${showExpanded ? "justify-between" : "justify-center"} mb-4 px-2`}>
                        {showExpanded && (
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                {theme === "dark" ? "Dark Mode" : "Light Mode"}
                            </span>
                        )}
                        <button
                            onClick={toggleTheme}
                            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
                            className={`relative flex-none w-14 h-7 flex items-center rounded-full transition-colors duration-300
                                ${theme === "dark"
                                    ? "bg-gradient-to-r from-indigo-500 to-purple-500"
                                    : "bg-gradient-to-r from-amber-400 to-orange-400"
                                }`}
                        >
                            <div
                                className={`absolute w-5 h-5 rounded-full bg-white shadow-lg flex items-center justify-center
                                    transform transition-all duration-300 ease-out
                                    ${theme === "dark" ? "translate-x-8" : "translate-x-1"}`}
                            >
                                {theme === "dark" ? (
                                    <FiMoon className="w-3 h-3 text-indigo-500" />
                                ) : (
                                    <FiSun className="w-3 h-3 text-amber-500" />
                                )}
                            </div>
                        </button>
                    </div>

                    {/* User Profile */}
                    <div
                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200
                            ${isLogoutScreenActive
                                ? "bg-gray-100 dark:bg-zinc-800"
                                : "hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                            }`}
                        onClick={handleProfileClick}
                        title={!showExpanded ? displayName : undefined}
                    >
                        {/* Avatar with gradient border */}
                        <div className="relative flex-none">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/50 p-0.5">
                                <div className="w-full h-full rounded-full bg-gray-100 dark:bg-zinc-700 flex items-center justify-center">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        {initials}
                                    </span>
                                </div>
                            </div>
                            {/* Online indicator */}
                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white dark:border-zinc-900" />
                        </div>

                        {showExpanded && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                    {displayName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                                    {roleLabel}
                                </p>
                            </div>
                        )}

                        {showExpanded && (
                            <FiChevronDown
                                className={`w-4 h-4 flex-none text-gray-400 transition-transform duration-200
                                    ${isLogoutScreenActive ? "rotate-180" : ""}`}
                            />
                        )}
                    </div>

                    {/* Logout Button */}
                    <div
                        className={`overflow-hidden transition-all duration-300 ease-out
                            ${showExpanded && isLogoutScreenActive ? "max-h-20 opacity-100 mt-2" : "max-h-0 opacity-0"}`}
                    >
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5
                                bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20
                                hover:from-red-100 hover:to-red-200 dark:hover:from-red-900/30 dark:hover:to-red-800/30
                                text-red-600 dark:text-red-400
                                rounded-xl transition-all duration-200
                                border border-red-200 dark:border-red-800/30"
                        >
                            <FiLogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Sign Out</span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
